// ── API ──────────────────────────────────────────────────────────────────────

/** Axios request timeout in milliseconds. */
export const API_TIMEOUT_MS = 15_000;

// ── Auth ─────────────────────────────────────────────────────────────────────

/** AsyncStorage key for the last email signed in with. Prefills the login field,
 *  and identifies the account for the passkey login that recovers an enrollment
 *  attempt on a device that already has a passkey. */
export const LAST_EMAIL_KEY = '@cynosure/last_email';

// ── Polling ──────────────────────────────────────────────────────────────────

/** How often the list screens re-evaluate time-derived statuses (an activity
 *  becoming "in progress" as its start time passes), in milliseconds. This is
 *  a local re-render, not a network call. */
export const POLL_INTERVAL_MS = 60_000;

/** How often a visible list screen refetches from the server so that a change
 *  made on another device shows up without user action. Three small GETs a
 *  minute; paused while the app is in the background. */
export const LIST_REFETCH_INTERVAL_MS = 60_000;

// ── Activity creation ─────────────────────────────────────────────────────────

/** Selectable duration chips on the activity-create screen (minutes). */
export const DURATION_OPTIONS_MINUTES = [15, 30, 45, 60, 90, 120] as const;

// ── Goal creation ────────────────────────────────────────────────────────────

/** Default number of days from today for a new goal's end date. */
export const DEFAULT_GOAL_DURATION_DAYS = 90;

// ── Time score ───────────────────────────────────────────────────────────────

export const SCORE_THRESHOLD_EXCELLENT = 80;
export const SCORE_THRESHOLD_GOOD = 60;
export const SCORE_THRESHOLD_AVERAGE = 40;

// ── Calendar / time ──────────────────────────────────────────────────────────

/** Milliseconds in one day (24 h). */
export const MS_PER_DAY = 86_400_000;

// ── UX feedback delays ───────────────────────────────────────────────────────

/** How long to show "Copied!" after a clipboard copy (ms). */
export const CLIPBOARD_FEEDBACK_MS = 2_000;

/** How long to show paste-success feedback (ms). */
export const PASTE_FEEDBACK_MS = 3_000;

// Key transfer expiry lives in `utils/key-transfer.ts`. It moved there when the
// v3 protocol started embedding the issue time in the payload and enforcing it
// in the parser — it is a property of the wire format now, not a UI setting.

// ── Legal ────────────────────────────────────────────────────────────────────

import { Linking, Platform } from 'react-native';

/** Fallback legal version, used only when version.json is unreachable on first launch
 *  (no stored acceptance yet). Bump when shipping a build that should be usable
 *  offline against a known-good manifest. */
export const LEGAL_FALLBACK_VERSION = '2026-04-19-r1';

/** AsyncStorage keys for the last-accepted terms and privacy versions. */
export const LEGAL_ACCEPTED_TERMS_KEY = '@cynosure/legal_accepted_terms_version';
export const LEGAL_ACCEPTED_PRIVACY_KEY = '@cynosure/legal_accepted_privacy_version';

/** Legacy single-version key. Read once on startup and migrated into the two
 *  split keys above so existing users aren't re-prompted. Safe to remove after
 *  the migration has rolled out to all users. */
export const LEGAL_LEGACY_ACCEPTED_KEY = '@cynosure/legal_accepted_version';

/** Remote manifest describing the current Terms / Privacy versions. */
export const LEGAL_VERSION_URL = 'https://about.a3rn.com/cynosure/legal/version.json';

/** Timeout for the version.json fetch. Short because the banner must not block UI. */
export const LEGAL_FETCH_TIMEOUT_MS = 5_000;

/** Public URLs hosting Cynosure's legal & support pages.
 *  These are the sources of truth referenced from App Store / Play Store listings. */
export const LEGAL_URLS = {
  privacy: 'https://about.a3rn.com/cynosure/privacy/',
  terms: 'https://about.a3rn.com/cynosure/terms/',
  support: 'https://about.a3rn.com/cynosure/support/',
} as const;

export type LegalUrlKey = keyof typeof LEGAL_URLS;

/** Open a legal page. Swallows errors (user decline, browser unavailable).
 *
 *  On native this uses an in-app browser sheet (SFSafariViewController /
 *  Custom Tabs) rather than Linking.openURL, which would background the app to
 *  Safari. Reading the terms should not cost the user their place — it matters
 *  most on sign-in, where the links sit mid-flow. On web there is no sheet to
 *  present, so a normal navigation is the right behaviour. */
export const openLegalUrl = (key: LegalUrlKey) => {
  const url = LEGAL_URLS[key];
  if (Platform.OS === 'web') {
    return Linking.openURL(url).catch(() => {
      /* no-op */
    });
  }
  // Required at call time, not imported at module scope: expo-web-browser is
  // ESM-only, and a top-level import of it drags every module that reaches
  // constants.ts (which is most of them) into jest's ESM transform problem.
  return import('expo-web-browser')
    .then((WebBrowser) => WebBrowser.openBrowserAsync(url))
    .catch(() => {
      /* no-op */
    });
};
